import React, { useCallback, useEffect, useState } from 'react'
import { Row, Col } from 'reactstrap'
import { Main, MainBody, MainTop, MainTopTitle } from '@/components/Layout'
import Resx from '@/components/Resx'
import LoadingIndicator from '@/components/LoadingIndicator'
import IndividualDetail from './individual-detail'
import { TabContent, TabNavItems, TabPane } from '@/components/tabs'
import { duplicateDetailTabs } from './duplicate-detail'
import { InfoContent, InfoLabel, InfoRow } from '@/components/InfoRow'
import { usePermissionChecker } from '@/hooks/usePermissionChecker'
import context from '@/context'
import { openErrorDialog } from '@/utils/dialogHelpers'
import { getStore } from '@/store'
import { push } from 'react-router-redux'
import { useLocalization } from '@/hooks/localization'

const DataCrossingComparison = React.memo(({ match }) => {
  const isGranted = usePermissionChecker()
  const { localize } = useLocalization()
  const id = match.params.id
  const [tabId, setTabId] = useState(duplicateDetailTabs.duplicates)
  const [comparisonResult, setComparisonResult] = useState(null)
  const [duplicateSetting, setDuplicateSetting] = useState(false)

  const { individual,
    normalizedIndividual,
    duplicateIndividual,
    normalizedDuplicateIndividual,
    duplicateAttributes,
    attributeWeight,
    totalWeight } = comparisonResult || {}

  useEffect(() => {
    if (id) {
      context.api.dataCrossingService.getComparison(id)
        .then(setComparisonResult)
        .catch(openErrorDialog)
    }
  }, [id])

  const toggleTab = useCallback(() => {
    setTabId(tabId === duplicateDetailTabs.duplicates
      ? duplicateDetailTabs.scoring : duplicateDetailTabs.duplicates)
  }, [tabId])

  const setComparison = useCallback((isSameIndividual) => {
    setDuplicateSetting(true)

    context.api.dataCrossingService.setIndividualComparison({ id, isSameIndividual })
      .then(() => {
        setDuplicateSetting(false)
        getStore().dispatch(push('/data-crossings-duplicates'))
      })
      .catch(openErrorDialog)
  }, [id])

  const mergeClick = useCallback(() => setComparison(true), [setComparison])
  const notDuplicateClick = useCallback(() => setComparison(false), [setComparison])

  return <Main>
    <MainTop>
      <MainTopTitle backUrl='/data-crossings-duplicates'>
        <Resx name='DataCrossings.Comparison' />
      </MainTopTitle>
    </MainTop>
    <MainBody className='comparison'>
      {isGranted('CanManageDataCrossingsDuplicates') && <div className='btn-group'>
        <button className='btn btn-md btn-danger' onClick={mergeClick}><Resx name='DataCrossings.MarkAsDuplicate' /></button>
        <button className='btn btn-md btn-primary btn-success' onClick={notDuplicateClick}><Resx name='DataCrossings.NotDuplicity' /></button>
      </div>}
      <TabNavItems activeTabId={tabId} onClick={toggleTab} items={[
        { id: duplicateDetailTabs.duplicates, text: 'DataCrossings.Duplicates' },
        { id: duplicateDetailTabs.scoring, text: 'DataCrossings.Scoring' }
      ]} />
      <TabContent activeTab={tabId}>
        <TabPane tabId={duplicateDetailTabs.duplicates}>
          {!comparisonResult
            ? <LoadingIndicator />
            : <React.Fragment>
              <Row>
                {duplicateSetting
                  ? <LoadingIndicator />
                  : <React.Fragment>
                    <Col sm='6'>
                      <IndividualDetail individual={individual} normalizedIndividual={normalizedIndividual} localize={localize} />
                    </Col>
                    <Col sm='6'>
                      <IndividualDetail individual={duplicateIndividual} normalizedIndividual={normalizedDuplicateIndividual} localize={localize} />
                    </Col>
                  </React.Fragment>
                }
              </Row>
            </React.Fragment>
          }
        </TabPane>
        <TabPane tabId={duplicateDetailTabs.scoring}>
          {!comparisonResult
            ? <LoadingIndicator />
            : <React.Fragment>
              <div><Resx name='DataCrossings.MatchedAttributes' /></div>
              {duplicateAttributes.map(weight =>
                <InfoRow key={weight.attributeName}>
                  <InfoLabel>{weight.attributeName}</InfoLabel>
                  <InfoContent>{weight.weight} ({(Math.round((weight.weight / comparisonResult.weight) * 10000) / 100)} %)</InfoContent>
                </InfoRow>)}
              <div><Resx name='DataCrossings.Summary' /></div>
              <InfoRow>
                <InfoLabel><Resx name='DataCrossings.TotalWeight' /></InfoLabel>
                <InfoContent>{comparisonResult.weight}</InfoContent>
              </InfoRow>
              <InfoRow>
                <InfoLabel><Resx name='DataCrossings.AllAttributesWeight' /></InfoLabel>
                <InfoContent>{attributeWeight}</InfoContent>
              </InfoRow>
              <InfoRow>
                <InfoLabel><Resx name='DataCrossings.AccordanceWeight' /></InfoLabel>
                <InfoContent>{totalWeight}%</InfoContent>
              </InfoRow>
            </React.Fragment>
          }
        </TabPane>
      </TabContent>
    </MainBody>
  </Main>
})

export { DataCrossingComparison }



// WEBPACK FOOTER //
// ./src/internal-app/pages/dataCrossing/comparison.js